import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { useLiteDiscipline } from "../../data/useLiteDiscipline";
import { behavior, byMood, byTag, byTradeIndex } from "../../lib/analytics";
import { summarize } from "../../lib/stats";
import { BucketTable, Compare, Insights } from "../../ui/analytics";
import { Card, Pnl } from "../../ui/ui";
import { compact, EmptyCard, insight, type TabProps } from "./shared";

export function PsychoTab({ trades }: TabProps) {
  const tl = useTranslation().lite;
  const t = tl.stats.psycho;
  const { fmt, moodByDay } = useLiteData();
  // L'objectif du jour est le goal PnL quotidien de la page Discipline ; sans lui, l'analyse reste vide.
  const { dailyPnlGoal: goal } = useLiteDiscipline();

  const b = behavior(trades, goal ?? Infinity);
  const idx = byTradeIndex(trades).map(x => ({ ...x, label: tl.stats.buckets.rank[x.key] }));
  const moods = byMood(trades, moodByDay).map(x => ({ ...x, label: tl.stats.buckets.mood[x.key] }));
  const tags = byTag(trades).map(x => ({ ...x, label: x.name }));
  const worstTag = tags.length ? tags[tags.length - 1] : undefined;
  const first = idx[0];
  const late = idx.length > 1 ? idx[idx.length - 1] : undefined;

  const insights = compact([
    b.afterLoss.count > 0 && insight(
      b.afterLoss.winRate < b.normal.winRate ? "bad" : "good",
      t.insightRevenge(fmt.pct(b.afterLoss.winRate), fmt.pct(b.normal.winRate), fmt.money(b.afterLoss.netPnl)),
    ),
    b.afterGoal.count > 0 && insight(
      b.afterGoal.netPnl < 0 ? "bad" : "info",
      t.insightAfterGoal(b.afterGoal.count, fmt.money(b.afterGoal.netPnl)) + (b.afterGoal.netPnl < 0 ? t.insightAfterGoalBad : ""),
    ),
    worstTag && worstTag.s.netPnl < 0 && insight("bad", t.insightTag(worstTag.name, fmt.money(worstTag.s.netPnl, { sign: false }), worstTag.s.count)),
    first && late && insight(
      first.s.expectancy > late.s.expectancy ? "info" : "good",
      t.insightRank(fmt.money(first.s.expectancy), fmt.money(late.s.expectancy), late.label),
    ),
  ]);

  return (
    <>
      <Insights items={insights} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        <Compare
          title={t.revenge}
          question={t.revengeQuestion}
          aLabel={t.revengeA} a={b.afterLoss}
          bLabel={t.revengeB} b={b.normal}
          verdict={b.afterLoss.count ? {
            tone: b.afterLoss.expectancy < b.normal.expectancy ? "bad" : "good",
            text: t.revengeVerdict(fmt.money(b.afterLoss.expectancy - b.normal.expectancy)),
          } : undefined}
        />
        <Compare
          title={t.twoLosses}
          question={t.twoLossesQuestion}
          aLabel={t.twoLossesA} a={b.afterTwoLosses}
          bLabel={t.twoLossesB} b={summarize(trades)}
        />
        <Card title={t.goal} subtitle={t.goalSubtitle}>
          <div className="font-mono tabular-nums text-2xl font-semibold"><Pnl value={b.afterGoal.netPnl} /></div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.goalMeta(b.afterGoal.count, b.afterGoal.count ? fmt.pct(b.afterGoal.winRate) : tl.stats.none)}
          </div>
          <p className="text-xs text-muted-foreground/70 mt-3">
            {goal === null ? t.goalNotSet : t.goalCurrent(fmt.money(goal, { sign: false }))}
          </p>
        </Card>
      </div>
      <div className="grid xl:grid-cols-2 gap-5">
        <BucketTable title={t.byRank} subtitle={t.byRankSubtitle} rows={idx} />
        {moods.length
          ? <BucketTable title={t.byMood} subtitle={t.byMoodSubtitle} rows={moods} />
          : <EmptyCard title={t.byMood} subtitle={t.byMoodSubtitle} emptyTitle={t.moodEmpty} emptyText={t.moodEmptyText} />}
      </div>
      {tags.length
        ? <BucketTable title={t.byTag} subtitle={t.byTagSubtitle} rows={tags} />
        : <EmptyCard title={t.byTag} subtitle={t.byTagSubtitle} emptyTitle={t.tagEmpty} emptyText={t.tagEmptyText} />}
    </>
  );
}
